// Client API push — wrappers vers /api/push/* + helpers Web Push API.
// P8.F.

import { apiCall } from "./client";

export interface PushSubscriptionRow {
  id: string;
  endpoint: string;
  user_agent: string | null;
  is_active: boolean;
  last_error_at: string | null;
  last_used_at: string | null;
  created_at: string;
}

export interface SubscribePayload {
  endpoint: string;
  keys: { p256dh: string; auth: string };
  user_agent?: string;
}

export async function subscribe(payload: SubscribePayload): Promise<{ id: string }> {
  const res = await apiCall<{ id: string }>("/api/push/subscribe", {
    method: "POST",
    body: payload,
    idempotencyKey: crypto.randomUUID(),
  });
  return res.data;
}

export async function unsubscribe(id: string): Promise<void> {
  await apiCall(`/api/push/subscribe/${id}`, { method: "DELETE" });
}

export async function listSubscriptions(): Promise<PushSubscriptionRow[]> {
  const res = await apiCall<PushSubscriptionRow[]>("/api/push/subscriptions", {
    query: { is_active: "true" },
  });
  return res.data;
}

export async function sendTest(
  message?: string,
): Promise<{ attempted: number; succeeded: number }> {
  const res = await apiCall<{ attempted: number; succeeded: number; expired: number }>(
    "/api/push/test",
    {
      method: "POST",
      body: { message: message ?? "Test MailRegent" },
      idempotencyKey: crypto.randomUUID(),
    },
  );
  return res.data;
}

// ============================================================================
// Helpers Web Push API navigateur
// ============================================================================

function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(normalized);
  const buffer = new ArrayBuffer(raw.length);
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

export function isPushSupported(): boolean {
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

export function isStandalone(): boolean {
  return window.matchMedia("(display-mode: standalone)").matches;
}

export function isIOS(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !("MSStream" in window);
}

export function permission(): NotificationPermission {
  return Notification.permission;
}

export async function requestPermission(): Promise<NotificationPermission> {
  return Notification.requestPermission();
}

/**
 * Souscrit le navigateur au push VAPID + envoie la sub au backend.
 * Idempotent : si deja souscrit, retourne la sub existante.
 */
export async function enablePush(
  vapidPublicKey: string,
): Promise<{ id: string; endpoint: string }> {
  if (!isPushSupported()) throw new Error("Web Push non supporte par ce navigateur");
  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  sub ??= await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
  });
  const json = sub.toJSON();
  const endpoint = json.endpoint;
  const keys = json.keys;
  if (!endpoint || !keys?.p256dh || !keys.auth) throw new Error("Souscription invalide");
  const result = await subscribe({
    endpoint,
    keys: { p256dh: keys.p256dh, auth: keys.auth },
    user_agent: navigator.userAgent,
  });
  return { id: result.id, endpoint };
}

export async function disablePush(): Promise<void> {
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (sub) await sub.unsubscribe();
}
